import React, { useState, useEffect, useCallback } from 'react';
import { ArrowLeft, Users, Copy, Trash2, Plus, Loader2, ShieldCheck, Edit3 } from 'lucide-react';
import { withBackend } from '../utils/api';
import { authHeaders } from '../utils/authHeaders';
import { toast } from 'sonner';

// r148: TEAM SUBSCRIPTIONS — the owner/admin surface: seats, invites,
// per-member usage, removal. A teamless user sees "create" and
// "join with a code" paths. Seat purchases ride the existing Stripe
// checkout with quantity.
// r150: embedded=true renders it as a section of the Plan & credits panel
// (its only home — the sidebar page was removed as redundant navigation).
export default function TeamPage({ userId, onBack, embedded = false }) {
  const [data, setData] = useState(null);   // { team, role, owner, members, seats_used } | { team: null }
  const [loading, setLoading] = useState(true);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteBusy, setInviteBusy] = useState(false);
  const [joinCode, setJoinCode] = useState('');
  const [joinBusy, setJoinBusy] = useState(false);
  const [creating, setCreating] = useState(false);
  const [seatBuyCount, setSeatBuyCount] = useState(10);
  // r154: a team can run on ANY paid tier — the picker lists the public
  // paid plans (falls back to Team-only if the fetch fails).
  const [seatTier, setSeatTier] = useState('team');
  const [tierOptions, setTierOptions] = useState([]);
  useEffect(() => {
    (async () => {
      try {
        const r = await fetch(withBackend('/api/billing/plans'));
        const d = await r.json();
        setTierOptions((d.plans || []).filter(p => (p.price_cents || 0) > 0));
      } catch { /* picker falls back to the Team plan */ }
    })();
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const resp = await fetch(withBackend('/api/teams/mine'), { headers: { ...(await authHeaders()) } });
      const d = await resp.json();
      if (!resp.ok) throw new Error(d.error || 'Failed to load team');
      setData(d);
    } catch (err) {
      // r152: embedded in the billing panel, a failed load (e.g. the teams
      // migration not yet run) must not toast every panel visitor — the
      // section just shows the create/join defaults (review #3).
      if (!embedded) toast.error(err.message);
      setData({ team: null });
    } finally {
      setLoading(false);
    }
  }, [embedded]);
  useEffect(() => { load(); }, [load]);

  const createTeam = async () => {
    setCreating(true);
    try {
      const resp = await fetch(withBackend('/api/teams/ensure'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(await authHeaders()) },
        body: JSON.stringify({}),
      });
      const d = await resp.json();
      if (!resp.ok) throw new Error(d.error || 'Failed to create team');
      toast.success('Team created');
      await load();
    } catch (err) { toast.error(err.message); }
    finally { setCreating(false); }
  };

  const invite = async () => {
    const email = inviteEmail.trim().toLowerCase();
    if (!email || !email.includes('@')) { toast.error('Enter a valid email'); return; }
    setInviteBusy(true);
    try {
      const resp = await fetch(withBackend(`/api/teams/${data.team.id}/invite`), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(await authHeaders()) },
        body: JSON.stringify({ email }),
      });
      const d = await resp.json();
      if (!resp.ok) throw new Error(d.error || 'Invite failed');
      setInviteEmail('');
      // r169: hand out a LINK — opening it joins automatically after login
      // (the code still works manually under Settings → Plan & credits).
      const inviteLink = `${window.location.origin}/?team_invite=${encodeURIComponent(d.invite_code)}`;
      try { await navigator.clipboard.writeText(inviteLink); } catch { /* clipboard optional */ }
      toast.success(`Invited ${email} — invite link copied. Send it to them; opening it joins the team automatically.`, { duration: 12000 });
      await load();
    } catch (err) { toast.error(err.message); }
    finally { setInviteBusy(false); }
  };

  const join = async () => {
    const code = joinCode.trim();
    if (!code) return;
    setJoinBusy(true);
    try {
      const resp = await fetch(withBackend('/api/teams/join'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(await authHeaders()) },
        body: JSON.stringify({ code }),
      });
      const d = await resp.json();
      if (!resp.ok) throw new Error(d.error || 'Join failed');
      toast.success(`Welcome to ${d.team?.name || 'the team'} — your plan is now ${d.team?.tier || 'team'}.`);
      setJoinCode('');
      await load();
    } catch (err) { toast.error(err.message); }
    finally { setJoinBusy(false); }
  };

  const removeMember = async (m) => {
    if (!window.confirm(`Remove ${m.email} from the team? Their plan drops back to Free.`)) return;
    try {
      const resp = await fetch(withBackend(`/api/teams/${data.team.id}/members/${m.id}`), {
        method: 'DELETE',
        headers: { ...(await authHeaders()) },
      });
      const d = await resp.json();
      if (!resp.ok) throw new Error(d.error || 'Remove failed');
      toast.success(`Removed ${m.email}`);
      await load();
    } catch (err) { toast.error(err.message); }
  };

  // r168: owner/admin sets a member's monthly credit cap (0 = paused,
  // empty = uncapped). window.prompt keeps it dependency-free.
  const setMemberCap = async (m) => {
    const raw = window.prompt(
      `Monthly credit limit for ${m.email}?\nEnter a number (0 pauses them), or leave empty for unlimited.`,
      m.member_credit_cap ?? '');
    if (raw === null) return; // cancelled
    const cap = raw.trim() === '' ? null : Math.max(0, parseInt(raw, 10) || 0);
    try {
      const resp = await fetch(withBackend(`/api/teams/${data.team.id}/members/${m.id}/cap`), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(await authHeaders()) },
        body: JSON.stringify({ cap }),
      });
      const d = await resp.json();
      if (!resp.ok) throw new Error(d.error || 'Could not set the limit');
      toast.success(cap === null ? `${m.email} is now unlimited`
        : cap === 0 ? `${m.email} is paused` : `${m.email} limited to ${cap.toLocaleString()} credits / month`);
      await load();
    } catch (err) { toast.error(err.message); }
  };

  // r168: OWNER-ONLY team rename.
  const renameTeam = async () => {
    const name = window.prompt('Team name:', data?.team?.name || '');
    if (name === null || !name.trim()) return;
    try {
      const resp = await fetch(withBackend(`/api/teams/${data.team.id}`), {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', ...(await authHeaders()) },
        body: JSON.stringify({ name: name.trim() }),
      });
      const d = await resp.json();
      if (!resp.ok) throw new Error(d.error || 'Rename failed');
      toast.success('Team renamed');
      await load();
    } catch (err) { toast.error(err.message); }
  };

  // r154: OWNER-ONLY role toggle — admins can invite/remove but never mint
  // other admins (the backend enforces this too).
  const setRole = async (m, role) => {
    try {
      const resp = await fetch(withBackend(`/api/teams/${data.team.id}/members/${m.id}/role`), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(await authHeaders()) },
        body: JSON.stringify({ role }),
      });
      const d = await resp.json();
      if (!resp.ok) throw new Error(d.error || 'Role change failed');
      toast.success(role === 'admin' ? `${m.email} is now an admin` : `${m.email} is a member again`);
      await load();
    } catch (err) { toast.error(err.message); }
  };

  const buySeats = async () => {
    try {
      const resp = await fetch(withBackend('/api/billing/checkout'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(await authHeaders()) },
        body: JSON.stringify({ user_id: userId, tier: seatTier, quantity: Math.max(1, Math.min(seatBuyCount, 100)) }),
      });
      const d = await resp.json();
      if (!resp.ok || !d.url) throw new Error(d.error || 'Checkout failed');
      window.location.href = d.url;
    } catch (err) { toast.error(err.message); }
  };

  const canManage = data?.role === 'owner' || data?.role === 'admin';

  const body = (
    <>
        {loading ? (
          <div className="flex items-center gap-2 text-muted-foreground py-16 justify-center">
            <Loader2 className="w-5 h-5 animate-spin" /> Loading...
          </div>
        ) : !data?.team ? (
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="border border-border rounded-xl p-5 bg-card space-y-3">
              <h2 className="font-semibold text-foreground">Start a team</h2>
              <p className="text-sm text-muted-foreground">
                Buy seats on any plan for your organization, then invite members. Seats
                follow your subscription quantity automatically, and promo codes apply
                at checkout.
              </p>
              <div className="flex items-center gap-2 flex-wrap">
                <input
                  type="number" min={2} max={100} value={seatBuyCount}
                  onChange={e => setSeatBuyCount(parseInt(e.target.value || '10', 10))}
                  className="w-20 px-2 py-1.5 rounded-lg border border-border bg-background text-sm"
                />
                {tierOptions.length > 0 && (
                  <select
                    value={seatTier} onChange={e => setSeatTier(e.target.value)}
                    className="px-2 py-1.5 rounded-lg border border-border bg-background text-sm"
                  >
                    {tierOptions.map(p => (
                      <option key={p.tier} value={p.tier}>{p.display_name}</option>
                    ))}
                  </select>
                )}
                <button onClick={buySeats} className="px-3 py-1.5 rounded-lg bg-primary text-primary-foreground text-sm font-medium">
                  Buy seats
                </button>
              </div>
              <button onClick={createTeam} disabled={creating} className="text-xs text-primary underline hover:no-underline disabled:opacity-50">
                {creating ? 'Creating...' : 'Already subscribed? Create your team'}
              </button>
            </div>
            <div className="border border-border rounded-xl p-5 bg-card space-y-3">
              <h2 className="font-semibold text-foreground">Join a team</h2>
              <p className="text-sm text-muted-foreground">Got an invite code from your team admin? Redeem it here.</p>
              <div className="flex items-center gap-2">
                <input
                  value={joinCode} onChange={e => setJoinCode(e.target.value)}
                  placeholder="Invite code"
                  className="flex-1 px-3 py-1.5 rounded-lg border border-border bg-background text-sm"
                />
                <button onClick={join} disabled={joinBusy || !joinCode.trim()} className="px-3 py-1.5 rounded-lg bg-primary text-primary-foreground text-sm font-medium disabled:opacity-50">
                  {joinBusy ? 'Joining...' : 'Join'}
                </button>
              </div>
            </div>
          </div>
        ) : (
          <div className="space-y-5">
            <div className="border border-border rounded-xl p-5 bg-card flex items-center justify-between flex-wrap gap-3">
              <div>
                <h2 className="font-semibold text-foreground">
                  {data.team.name}
                  {data?.role === 'owner' && (
                    <button onClick={renameTeam} className="ml-1.5 text-muted-foreground hover:text-foreground align-middle" title="Rename team">
                      <Edit3 className="w-3.5 h-3.5 inline" />
                    </button>
                  )}
                </h2>
                <p className="text-xs text-muted-foreground">
                  Plan: <span className="font-medium">{data.team.tier}</span> · {data.seats_used}/{data.team.seats} seats used
                </p>
              </div>
              {canManage && (
                <div className="flex items-center gap-2">
                  <input
                    value={inviteEmail} onChange={e => setInviteEmail(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter') invite(); }}
                    placeholder="teammate@company.com"
                    className="w-56 px-3 py-1.5 rounded-lg border border-border bg-background text-sm"
                  />
                  <button onClick={invite} disabled={inviteBusy} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-primary text-primary-foreground text-sm font-medium disabled:opacity-50">
                    <Plus className="w-3.5 h-3.5" /> {inviteBusy ? 'Inviting...' : 'Invite'}
                  </button>
                </div>
              )}
            </div>

            <div className="border border-border rounded-xl overflow-x-auto bg-card">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border text-left text-xs text-muted-foreground">
                    <th className="px-4 py-2.5 font-medium">Member</th>
                    <th className="px-3 py-2.5 font-medium">Role</th>
                    <th className="px-3 py-2.5 font-medium">Status</th>
                    <th className="px-3 py-2.5 font-medium text-right">Credits (mo)</th>
                    <th className="px-3 py-2.5 font-medium text-right">Generations (mo)</th>
                    {canManage && <th className="px-3 py-2.5" />}
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-border/50">
                    <td className="px-4 py-2.5 font-medium text-foreground">{data.owner?.email || 'Owner'}</td>
                    <td className="px-3 py-2.5"><span className="inline-flex items-center gap-1 text-xs text-primary"><ShieldCheck className="w-3.5 h-3.5" /> owner</span></td>
                    <td className="px-3 py-2.5 text-xs">active</td>
                    <td className="px-3 py-2.5 text-right tabular-nums">{data.owner?.month_credits ?? 0}</td>
                    <td className="px-3 py-2.5 text-right tabular-nums">{data.owner?.month_generations ?? 0}</td>
                    {canManage && <td />}
                  </tr>
                  {data.members.map(m => (
                    <tr key={m.id} className="border-b border-border/50 last:border-0 hover:bg-muted/30">
                      <td className="px-4 py-2.5 text-foreground">{m.email}</td>
                      <td className="px-3 py-2.5 text-xs">{m.role}</td>
                      <td className="px-3 py-2.5">
                        <span className={`px-2 py-0.5 rounded-full text-[11px] font-medium ${m.status === 'active' ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400' : 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400'}`}>
                          {m.status}
                        </span>
                        {m.status === 'invited' && m.invite_code && (
                          <button
                            onClick={async () => { const link = `${window.location.origin}/?team_invite=${encodeURIComponent(m.invite_code)}`; try { await navigator.clipboard.writeText(link); toast.success('Invite link copied'); } catch { toast.info(`Code: ${m.invite_code}`); } }}
                            className="ml-2 text-[11px] text-primary underline hover:no-underline inline-flex items-center gap-0.5"
                          >
                            <Copy className="w-3 h-3" /> link
                          </button>
                        )}
                      </td>
                      <td className="px-3 py-2.5 text-right tabular-nums">
                        {m.month_credits}
                        {m.member_credit_cap != null && (
                          <span className={`ml-1 text-[11px] ${m.member_credit_cap === 0 ? 'text-destructive' : 'text-muted-foreground'}`}>
                            / {m.member_credit_cap === 0 ? 'paused' : m.member_credit_cap.toLocaleString()}
                          </span>
                        )}
                        {canManage && m.status === 'active' && (
                          <button
                            onClick={() => setMemberCap(m)}
                            className="ml-1.5 text-[11px] text-primary underline hover:no-underline align-middle"
                            title="Set a monthly credit limit (0 pauses this member)"
                          >
                            limit
                          </button>
                        )}
                      </td>
                      <td className="px-3 py-2.5 text-right tabular-nums">{m.month_generations}</td>
                      {canManage && (
                        <td className="px-3 py-2.5 text-right whitespace-nowrap">
                          {data?.role === 'owner' && m.status === 'active' && (
                            <button
                              onClick={() => setRole(m, m.role === 'admin' ? 'member' : 'admin')}
                              className="text-[11px] text-primary underline hover:no-underline mr-2 align-middle"
                              title="Admins can invite and remove members"
                            >
                              {m.role === 'admin' ? 'Revoke admin' : 'Make admin'}
                            </button>
                          )}
                          <button onClick={() => removeMember(m)} className="text-destructive/70 hover:text-destructive align-middle" title="Remove — their plan drops to Free">
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </td>
                      )}
                    </tr>
                  ))}
                  {data.members.length === 0 && (
                    <tr><td colSpan={canManage ? 6 : 5} className="px-4 py-6 text-center text-sm text-muted-foreground">No members yet — invite your first teammate above.</td></tr>
                  )}
                </tbody>
              </table>
            </div>

            {canManage && (
              <p className="text-xs text-muted-foreground">
                Need more seats? Adjust the subscription quantity in Stripe (Manage subscription above), or come back after checkout — seats sync automatically.
              </p>
            )}
          </div>
        )}
    </>
  );

  if (embedded) {
    return (
      <div className="mt-6 pt-5 border-t border-border">
        <div className="flex items-center gap-2 mb-1">
          <Users className="w-4 h-4 text-primary" />
          <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Team</h3>
        </div>
        <p className="text-[11px] text-muted-foreground mb-3">Seats, invites, and member usage.</p>
        {body}
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-4xl mx-auto px-6 py-8">
        <div className="flex items-center gap-3 mb-6">
          <button onClick={onBack} className="text-muted-foreground hover:text-foreground">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-blue-500 to-indigo-500 flex items-center justify-center">
            <Users className="w-5 h-5 text-white" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-foreground">Team</h1>
            <p className="text-xs text-muted-foreground">Seats, invites, and member usage</p>
          </div>
        </div>
        {body}
      </div>
    </div>
  );
}
